import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from "@mui/material";
import {
  EventNoteOutlined,
  AccessTimeOutlined,
  CheckCircleOutlined,
  PendingOutlined,
  NotificationsOutlined,
  BusinessOutlined,
  PersonOutlined,
  WorkOutlineOutlined,
} from "@mui/icons-material";
import { selectUser } from "../../../store/slices/authSlice";
import { useAuth } from "../auth/AuthGuard";
import { ROUTES } from "../../../constants";

// Import APIs for personal data
import { leavesAPI } from "../../../services/api/leaves.api";
import attendanceAPI from "../../../services/api/attendance.api";
import activityLogsAPI from "../../../services/api/activityLogs.api";
import usersAPI from "../../../services/api/users.api";

const PersonalDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { isAdmin, isHR, isManager } = useAuth();

  // Fetch personal data
  const { data: myLeavesData, isLoading: leavesLoading } = useQuery({
    queryKey: ["my-leaves", "personal-dashboard"],
    queryFn: () => leavesAPI.getMyLeaves({ limit: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: leaveBalanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ["leave-balance", "personal-dashboard"],
    queryFn: () => leavesAPI.getLeaveBalance(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch today's attendance
  const { data: attendanceData, isLoading: attendanceLoading } = useQuery({
    queryKey: ["my-attendance", "personal-dashboard", "today"],
    queryFn: () => {
      const today = new Date().toISOString().split('T')[0];
      return attendanceAPI.getMyAttendance({ start_date: today, end_date: today, limit: 1 });
    },
    staleTime: 1 * 60 * 1000, // 1 minute for attendance
  });

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ["my-activities", "personal-dashboard"],
    queryFn: () => activityLogsAPI.getRecentActivities(10),
    staleTime: 1 * 60 * 1000,
  });

  // Fetch approval statistics if user is an approver
  const { data: approvalStatsData, isLoading: approvalStatsLoading } = useQuery({
    queryKey: ["approval-statistics", "personal-dashboard"],
    queryFn: () => leavesAPI.getApprovalStatistics(),
    enabled: isAdmin || isHR || isManager,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch current user's full profile to get department (same as employee list)
  const { data: currentUserProfileData, isLoading: profileLoading } = useQuery({
    queryKey: ["current-user-profile", user?.id],
    queryFn: () => usersAPI.getUserById(user?.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Debug: Log profile data
  if (import.meta.env.DEV && currentUserProfileData) {
    console.log('Current user profile data:', currentUserProfileData);
  }

  // Calculate personal stats - Fix leave counting
  // API service returns response.data which is: { success: true, message: "...", data: { applications: [...], pagination: {...} } }
  // useQuery wraps it, so myLeavesData = { success: true, message: "...", data: { applications: [...], pagination: {...} } }
  let myLeaves = [];
  if (myLeavesData) {
    // Try different response structures
    if (myLeavesData.data?.applications && Array.isArray(myLeavesData.data.applications)) {
      myLeaves = myLeavesData.data.applications;
    } else if (myLeavesData.data?.data?.applications && Array.isArray(myLeavesData.data.data.applications)) {
      myLeaves = myLeavesData.data.data.applications;
    } else if (Array.isArray(myLeavesData.data)) {
      myLeaves = myLeavesData.data;
    } else if (Array.isArray(myLeavesData.applications)) {
      myLeaves = myLeavesData.applications;
    } else if (Array.isArray(myLeavesData)) {
      myLeaves = myLeavesData;
    }
  }

  const pendingLeaves = myLeaves.filter(
    (leave) => leave.approval_status === "pending"
  ).length;
  
  const approvedLeaves = myLeaves.filter(
    (leave) => {
      const status = leave.approval_status?.toLowerCase();
      return status === "approved" || 
             status === "approved by supervisor" ||
             status === "approved by hr";
    }
  ).length;

  // Leave balance structure: { success: true, message: "...", data: { balances: [...] } }
  const leaveBalance = leaveBalanceData?.data?.balances || 
                       (Array.isArray(leaveBalanceData?.data) ? leaveBalanceData.data : []) ||
                       (Array.isArray(leaveBalanceData?.balances) ? leaveBalanceData.balances : []) ||
                       [];
  const totalLeaveBalance = Array.isArray(leaveBalance) 
    ? leaveBalance.reduce((sum, balance) => sum + (balance?.remaining || 0), 0)
    : 0;

  const recentActivities = activitiesData?.data || [];
  const approvalStats = approvalStatsData?.data || { pending: 0, approved: 0, rejected: 0 };

  // Get today's attendance status
  // API service returns response.data which is: { success: true, message: "...", data: { attendance: [...] } }
  let todayAttendance = null;
  if (attendanceData) {
    if (Array.isArray(attendanceData.data?.attendance)) {
      todayAttendance = attendanceData.data.attendance[0] || null;
    } else if (attendanceData.data?.data?.attendance && Array.isArray(attendanceData.data.data.attendance)) {
      todayAttendance = attendanceData.data.data.attendance[0] || null;
    } else if (Array.isArray(attendanceData.data)) {
      todayAttendance = attendanceData.data[0] || null;
    } else if (Array.isArray(attendanceData.attendance)) {
      todayAttendance = attendanceData.attendance[0] || null;
    }
  }
  
  const attendanceStatus = todayAttendance 
    ? (todayAttendance.clock_out_time ? "Present" : "Clocked In")
    : "Not Clocked In";

  // Get user department - use same approach as employee list
  // Backend now flattens department to user.department (string) in getUserById
  // Handle different response structures: { data: { ... } } or { ... } directly
  const currentUserProfile = currentUserProfileData?.data || currentUserProfileData;
  
  // Debug: Log department extraction
  if (import.meta.env.DEV) {
    console.log('Current user profile:', currentUserProfile);
    console.log('Profile department:', currentUserProfile?.department);
    console.log('Redux user department:', user?.department);
    console.log('Redux user basicEmployeeData:', user?.basicEmployeeData?.department);
  }
  
  const userDepartment = currentUserProfile?.department || 
                         user?.department || 
                         currentUserProfile?.basicEmployeeData?.department?.department_name ||
                         user?.basicEmployeeData?.department?.department_name || 
                         currentUserProfile?.basicEmployeeData?.department_name ||
                         user?.basicEmployeeData?.department_name ||
                         "N/A";

  const getUserGreeting = () => {
    const hour = new Date().getHours();
    let greeting = "Good morning";
    if (hour >= 12 && hour < 17) greeting = "Good afternoon";
    else if (hour >= 17) greeting = "Good evening";

    const firstName = user?.first_name || user?.name || "User";
    return `${greeting}, ${firstName}!`;
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString("en-US", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Minimalistic stat card component
  const StatCard = ({ title, value, subtitle, icon, loading, valueSize = "h4", isDepartment = false }) => {
    // Check if value is "N/A" or contains "Not" to use smaller text
    const isSmallText = typeof value === 'string' && (value === "N/A" || value.includes("Not"));
    
    // Determine the appropriate variant and size
    let variant = valueSize;
    let fontSize = null;
    
    if (isDepartment) {
      // Department names should be smaller and can wrap
      variant = "body1";
      fontSize = '0.9rem';
    } else if (isSmallText) {
      variant = "body2";
      fontSize = '0.875rem';
    }
    
    return (
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          height: '100%',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 1,
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {icon && (
                <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  {icon}
                </Box>
              )}
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography 
                  variant={variant} 
                  sx={{ 
                    fontWeight: isSmallText ? 500 : 600, 
                    color: isSmallText ? 'text.secondary' : 'text.primary',
                    fontSize: fontSize,
                    ...(isDepartment && {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    })
                  }}
                >
                  {value}
                </Typography>
              )}
            </Box>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
          {getUserGreeting()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getCurrentDate()} • {getCurrentTime()}
        </Typography>
      </Box>

      {/* Personal Statistics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Leave Balance"
            value={totalLeaveBalance}
            subtitle="days remaining"
            icon={<EventNoteOutlined sx={{ fontSize: 20 }} />}
            loading={balanceLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's Status"
            value={attendanceStatus.length > 15 ? attendanceStatus.substring(0, 15) + "..." : attendanceStatus}
            subtitle="attendance"
            icon={<AccessTimeOutlined sx={{ fontSize: 20 }} />}
            loading={attendanceLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Department"
            value={userDepartment}
            subtitle="your department"
            icon={<BusinessOutlined sx={{ fontSize: 20 }} />}
            loading={false}
            valueSize="body1"
            isDepartment={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved Leaves"
            value={approvedLeaves}
            subtitle="this year"
            icon={<CheckCircleOutlined sx={{ fontSize: 20 }} />}
            loading={leavesLoading}
          />
        </Grid>
      </Grid>

      {/* Leave Statistics Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Pending Leaves"
            value={pendingLeaves}
            subtitle="awaiting approval"
            icon={<PendingOutlined sx={{ fontSize: 20 }} />}
            loading={leavesLoading}
          />
        </Grid>
        {(isAdmin || isHR || isManager) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Pending Approvals"
              value={approvalStats.pending}
              subtitle="awaiting your action"
              icon={<PendingOutlined sx={{ fontSize: 20 }} />}
              loading={approvalStatsLoading}
            />
          </Grid>
        )}
        {(isAdmin || isHR || isManager) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="My Approved"
              value={approvalStats.approved}
              subtitle="leaves you approved"
              icon={<CheckCircleOutlined sx={{ fontSize: 20 }} />}
              loading={approvalStatsLoading}
            />
          </Grid>
        )}
      </Grid>

      {/* Approval Statistics for Approvers */}
      {(isAdmin || isHR || isManager) && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  My Approval Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 600, color: 'success.main', mb: 0.5 }}>
                        {approvalStatsLoading ? <CircularProgress size={32} /> : approvalStats.approved}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Approved
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 600, color: 'error.main', mb: 0.5 }}>
                        {approvalStatsLoading ? <CircularProgress size={32} /> : approvalStats.rejected}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Rejected
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
                        {approvalStatsLoading ? <CircularProgress size={32} /> : approvalStats.pending}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(ROUTES.LEAVE_APPROVALS || "/leaves/approvals")}
                  >
                    View All Approvals
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Activity */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Recent Activities
              </Typography>
              {activitiesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : recentActivities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No recent activities
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {recentActivities.slice(0, 5).map((activity, index) => (
                    <React.Fragment key={activity.id || index}>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.hover' }}>
                            <NotificationsOutlined sx={{ fontSize: 18 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {activity.action || "Activity"}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {activity.description || "Activity performed"} • {new Date(activity.created_at).toLocaleString()}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Quick Actions
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EventNoteOutlined />}
                  onClick={() => navigate(ROUTES.LEAVE_CREATE || "/leaves/create")}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Apply for Leave
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AccessTimeOutlined />}
                  onClick={() => navigate(ROUTES.ATTENDANCE || "/attendance")}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  View Attendance
                </Button>
                {(isAdmin || isHR || isManager) && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<CheckCircleOutlined />}
                    onClick={() => navigate(ROUTES.LEAVE_APPROVALS || "/leaves/approvals")}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Approve Leaves
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalDashboard;
